import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Clock, Link2, Pause } from "lucide-react";

import { disposeActiveStream } from "@/anticheat/camera";
import {
  exitFullscreen,
  isFullscreen,
  isFullscreenSupported,
} from "@/anticheat/fullscreen";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { loadAttemptId, loadCandidateMeta } from "@/lib/api";
import { formatDateTime, formatHMS } from "@/lib/utils";
import type { PausedAttempt } from "@/lib/candidateError";

// EF-2 — "Your test is paused". Reached from Question.tsx after POST /pause
// lands (router state = the pause response), or from ANY mid-test route whose
// request answered 409 `attempt_paused` (state rebuilt from the 409 by
// `pausedAttemptOf`). Router state survives a reload (memory
// project_router_state_survives_reload), so a refresh keeps this screen.
//
// Nothing anti-cheat is mounted here (that all lives in Question.tsx), so a
// pause emits no tab / fullscreen / camera events by construction — the same
// posture as the CP-18 break screen. The clock is frozen server-side; this
// screen renders the frozen remaining time and never ticks it.
//
// "Resume now" goes back through the GATE (`state.from === "resume"`): the
// camera must be re-acquired and fullscreen re-entered on a fresh gesture
// before the clock restarts, and the gate already owns that chain. Closing
// the tab is fine — opening the same invitation link on any device lands on
// the gate's resume card (the server hands the attempt back on /preview).
export interface PausedState {
  position: number;
  pausedAt: string | null;
  resumeBy: string | null;
  remainingSeconds: number | null;
}

/**
 * The 409's facts as this screen's router state.
 *
 * `navigate(..., { state })` is untyped, and the same four-line snake→camel
 * mapping was written out character-for-character at all three entry points
 * (Question, Review, SectionReview). So a field added to `PausedState` would
 * have compiled everywhere while silently arriving `undefined` on two of the
 * three paths — and this screen bounces to the gate when the state it needs is
 * missing, which would have looked like a mid-test gate bounce on one route
 * only (SIMP-C, 2026-09-12 pre-ship). One typed converter, three callers.
 */
export function pausedStateOf(paused: PausedAttempt): PausedState {
  return {
    position: paused.position,
    pausedAt: paused.paused_at,
    resumeBy: paused.resume_by,
    remainingSeconds: paused.remaining_seconds,
  };
}

export default function Paused() {
  const { token = "demo" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as PausedState | null;
  const attemptId = loadAttemptId(token);
  const meta = useMemo(() => loadCandidateMeta(token), [token]);
  const usable =
    Boolean(attemptId) && state !== null && typeof state?.position === "number";

  // No handle, or no state (a pasted URL, cleared storage): the gate sorts it
  // out. With a stored id it retries question 1 and the 409 lands back here
  // WITH state; without one, an invitation link's /preview offers the resume
  // card. Plain replace, no `from` marker — this is not a stuck bounce.
  useEffect(() => {
    if (!usable) navigate(`/t/${token}`, { replace: true });
  }, [usable, navigate, token]);

  // The camera light must not stay on for hours, and the candidate must be
  // able to leave the window. Question.tsx's fullscreen hook unmounted before
  // this effect runs, so the exit below emits no `fullscreen_exit`. Both
  // calls are idempotent (a 409-rebuild may reach here with neither active).
  useEffect(() => {
    disposeActiveStream();
    if (isFullscreenSupported() && isFullscreen()) {
      void exitFullscreen().catch(() => {});
    }
  }, []);

  if (!usable || !state) return null;

  const remaining =
    state.remainingSeconds !== null ? formatHMS(state.remainingSeconds * 1000) : null;
  const resumeBy = state.resumeBy ? formatDateTime(state.resumeBy) : null;

  return (
    <div className="flex min-h-full items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
            <Pause className="h-6 w-6 text-info" aria-hidden="true" />
          </div>
          <CardTitle as="h1" className="text-xl">
            Your test is paused
          </CardTitle>
          <CardDescription>
            {meta?.test_name ? `${meta.test_name} — ` : ""}the clock is stopped.
            Your answers so far are saved.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {remaining !== null && (
            <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-3">
              <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="text-muted-foreground">
                Time remaining when you resume:{" "}
                <span
                  className="font-mono font-medium tabular-nums text-foreground"
                  role="timer"
                  aria-label="Time remaining (paused)"
                >
                  {remaining}
                </span>
              </span>
            </div>
          )}
          <div className="flex items-start gap-3 rounded-md border border-info/40 bg-info/5 px-3 py-3">
            <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden="true" />
            <div className="space-y-1 text-muted-foreground">
              <p>
                You can close this tab. To continue, open the same link from
                your invitation email — on this device or another — and click
                Resume. The clock starts again only when you do.
              </p>
              {resumeBy && (
                <p>
                  <span className="font-medium text-foreground">
                    Resume by {resumeBy}.
                  </span>{" "}
                  After that the test can no longer be reopened.
                </p>
              )}
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button
            type="button"
            className="w-full cursor-pointer min-h-11"
            onClick={() =>
              navigate(`/t/${token}`, {
                state: {
                  from: "resume",
                  position: state.position,
                  pausedAt: state.pausedAt,
                  resumeBy: state.resumeBy,
                  remainingSeconds: state.remainingSeconds,
                },
              })
            }
          >
            Resume now
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
